import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["menuItems", "focusFilter", "enableFilter", "filterPlaceholder", "notFoundMessage", "className", "groupClassName", "responsive", "autoClose", "filterKey"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/fp/isEmpty';
import debounce from 'lodash/fp/debounce';
import classNames from 'classnames';
import useEffectOnce from '../../hooks/useEffectOnce';
import ClearableInput from '../clearableInput/ClearableInput';
import ExpanderPanel from '../expander/ExpanderPanel';

// @screen-ls: 480px;
// @screen-sm: 768px;

var RESIZE_THROTTELING = 10;
var MOBILE_MAX_WIDTH = 580;
var filterMenuItems = function filterMenuItems(items, value, filterKey) {
  return items.map(function (menuGroupItem) {
    var groupItems = menuGroupItem.navItems;
    var navItems = groupItems.filter(function (item) {
      var _item$filterKey;
      return (_item$filterKey = item[filterKey]) === null || _item$filterKey === void 0 ? void 0 : _item$filterKey.toLowerCase().includes(value.toLowerCase());
    });
    return _objectSpread(_objectSpread({}, menuGroupItem), {}, {
      navItems: navItems
    });
  });
};
var ListMenuHeader = function ListMenuHeader(_ref) {
  var group = _ref.group,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    groupNavItem = _ref.groupNavItem;
  if (!group && !groupNavItem) {
    return null;
  }
  return /*#__PURE__*/React.createElement("li", {
    className: "ListMenuHeader"
  }, groupNavItem ? groupNavItem : /*#__PURE__*/React.createElement("span", {
    className: className
  }, group));
};
var ListMenuItem = function ListMenuItem(_ref2) {
  var disabled = _ref2.disabled,
    _ref2$isSubItem = _ref2.isSubItem,
    isSubItem = _ref2$isSubItem === void 0 ? false : _ref2$isSubItem,
    children = _ref2.children;
  var classes = classNames(disabled && 'disabled', isSubItem && 'subitem');
  return /*#__PURE__*/React.createElement("li", {
    className: classes
  }, children);
};
var ListMenuGroup = function ListMenuGroup(_ref3) {
  var menuGroup = _ref3.menuGroup,
    className = _ref3.className;
  if (!menuGroup.navItems.length) {
    return null;
  }
  return /*#__PURE__*/React.createElement("ul", {
    className: "ListMenuGroup ".concat(className)
  }, /*#__PURE__*/React.createElement(ListMenuHeader, menuGroup), menuGroup.navItems.map(function (item) {
    return /*#__PURE__*/React.createElement(ListMenuItem, {
      key: item.key,
      disabled: item.disabled,
      isSubItem: item.isSubItem
    }, item.item);
  }));
};
var NotFoundMessage = function NotFoundMessage(_ref4) {
  var notFoundMessage = _ref4.notFoundMessage;
  return /*#__PURE__*/React.createElement("div", {
    className: "padding-top-25 text-center text-color-gray"
  }, notFoundMessage);
};
var ListMenu = function ListMenu(props) {
  var menuItems = props.menuItems,
    focusFilter = props.focusFilter,
    enableFilter = props.enableFilter,
    filterPlaceholder = props.filterPlaceholder,
    notFoundMessage = props.notFoundMessage,
    className = props.className,
    groupClassName = props.groupClassName,
    responsive = props.responsive,
    autoClose = props.autoClose,
    filterKey = props.filterKey,
    remainingProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    filterValue = _useState2[0],
    setFilterValue = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isMobileMode = _useState4[0],
    setIsMobileMode = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isExpanderOpen = _useState6[0],
    setIsExpanderOpen = _useState6[1];
  var _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    mobileHeader = _useState8[0],
    setMobileHeader = _useState8[1];
  var inputRef = useRef();
  var listRef = useRef();
  var buildMobileHeader = function buildMobileHeader() {
    if (responsive && listRef.current) {
      var _listRef$current$getE = listRef.current.getElementsByClassName('active'),
        _listRef$current$getE2 = _slicedToArray(_listRef$current$getE, 1),
        activeElement = _listRef$current$getE2[0];
      setMobileHeader( /*#__PURE__*/React.createElement("div", {
        className: "display-flex align-items-center"
      }, /*#__PURE__*/React.createElement("span", {
        className: "rioglyph rioglyph-menu-hamburger margin-right-10"
      }), /*#__PURE__*/React.createElement("span", null, activeElement && activeElement.innerText)));
    }
  };
  var handleMobileMode = function handleMobileMode() {
    return setIsMobileMode(window.innerWidth < MOBILE_MAX_WIDTH);
  };
  useEffectOnce(function () {
    var handleKeyDown = function handleKeyDown(event) {
      // clear on esc
      if (event.keyCode === 27 && enableFilter && inputRef.current === document.activeElement) {
        setFilterValue('');
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return function () {
      document.removeEventListener('keydown', handleKeyDown);
    };
  });
  useEffectOnce(function () {
    handleMobileMode();
    var handleResize = debounce(RESIZE_THROTTELING)(function () {
      handleMobileMode();
      buildMobileHeader();
    });
    window.addEventListener('resize', handleResize);
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  });
  useLayoutEffect(function () {
    buildMobileHeader();
  }, [menuItems]);
  useEffectOnce(function () {
    focusFilter && inputRef.current && inputRef.current.focus();
  });
  var handleClear = function handleClear() {
    return focusFilter && inputRef.current && inputRef.current.focus();
  };
  var handleFilterChange = function handleFilterChange(value) {
    return setFilterValue(value);
  };
  var hasMenuItems = function hasMenuItems(items) {
    return items.find(function (menuGroup) {
      return !isEmpty(menuGroup.navItems);
    });
  };
  var filteredMenuItems = filterMenuItems(menuItems, filterValue, filterKey);
  var handleExpanderBodyClick = function handleExpanderBodyClick(event) {
    var isListItem = event.target.parentElement.tagName.toLowerCase() === 'li';
    autoClose && isListItem && setIsExpanderOpen(false);
  };
  var formClassNames = classNames('form-group margin-bottom-5', 'padding-left-15', 'padding-right-15', 'padding-bottom-15', 'position-sticky', 'top-0');
  var listMenu = /*#__PURE__*/React.createElement("div", _extends({}, remainingProps, {
    className: "ListMenu ".concat(className),
    ref: listRef
  }), enableFilter && /*#__PURE__*/React.createElement("div", {
    className: formClassNames
  }, /*#__PURE__*/React.createElement("div", {
    className: "input-group width-100pct"
  }, /*#__PURE__*/React.createElement("span", {
    className: "input-group-addon"
  }, /*#__PURE__*/React.createElement("span", {
    className: "rioglyph rioglyph-search",
    "aria-hidden": "true"
  })), /*#__PURE__*/React.createElement(ClearableInput, {
    value: filterValue,
    inputRef: inputRef,
    placeholder: filterPlaceholder,
    onChange: handleFilterChange,
    onClear: handleClear
  }))), !hasMenuItems(filteredMenuItems) && /*#__PURE__*/React.createElement(NotFoundMessage, {
    notFoundMessage: notFoundMessage
  }), filteredMenuItems.map(function (menuGroup, index) {
    return /*#__PURE__*/React.createElement(ListMenuGroup, {
      key: index,
      className: groupClassName,
      menuGroup: menuGroup
    });
  }));
  if (responsive && isMobileMode) {
    return /*#__PURE__*/React.createElement(ExpanderPanel, {
      title: mobileHeader,
      bsStyle: "default",
      open: isExpanderOpen,
      onToggle: function onToggle() {
        return setIsExpanderOpen(!isExpanderOpen);
      },
      mountOnEnter: false,
      unmountOnExit: false,
      className: "shadow-default"
    }, /*#__PURE__*/React.createElement("div", {
      onClick: handleExpanderBodyClick
    }, listMenu));
  }
  return listMenu;
};
ListMenu.defaultProps = {
  menuItems: [],
  enableFilter: false,
  focusFilter: false,
  className: '',
  groupClassName: '',
  responsive: true,
  autoClose: true,
  filterKey: 'key'
};
ListMenu.propTypes = {
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    group: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    groupNavItem: PropTypes.node,
    navItems: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string.isRequired,
      item: PropTypes.node.isRequired,
      disabled: PropTypes.bool
    })).isRequired,
    isSubItem: PropTypes.bool
  })).isRequired,
  enableFilter: PropTypes.bool,
  focusFilter: PropTypes.bool,
  filterKey: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  notFoundMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  groupClassName: PropTypes.string,
  className: PropTypes.string,
  responsive: PropTypes.bool,
  autoClose: PropTypes.bool
};
export default ListMenu;